import { setNestedObjectValues, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Paths } from "src/navigation";
import { useAccordionStore, useCurrentStepStore } from "src/storage";
import {
  useDraftCAIStore,
  useDraftIDStore,
} from "src/storage/formik/formik-draft.store";
import {
  AccidentMapper,
  BozzaStepDTO,
  CircumstancesMapper,
  FormikDraftCAI,
  FormikStep,
  VehicleMapper,
  VehicleType,
} from "src/types";
import { useValidationSchemas } from "./handlers/draft-validation-schemas.hook";
import { useScrollToError } from "./handlers/scroll-to-error.hook";
import {
  useGetAccidentDataDraft,
  useGetVehicleDraft,
  usePutAccidentDataDraft,
  usePutVehicleDraft,
  useVerifyResponsibility,
} from "src/networking";
import { mapDriverResponsabilitiesResponse, useBoolean } from "src/utils";

export const useDataEntryWrapper = () => {
  const { t } = useTranslation();

  //General navigation logic
  const { currentStep } = useDataEntryNavigation();

  //Formik logic
  const {
    formikCAI,
    validationSchema,
    responsabilityModalVisibility,
    responsabilityModalMessage,
    hideResponsabilityModalVisibility,
    onGoBackClick,
    updateAccidentDataDraft,
    handleFormiksubmit,
  } = useDataEntryFormik();

  return {
    formikCAI,
    validationSchema,
    responsabilityModalVisibility,
    responsabilityModalMessage,
    currentStep,
    t,
    onGoBackClick,
    handleFormiksubmit,
    hideResponsabilityModalVisibility,
    updateAccidentDataDraft,
  };
};

export const useDataEntryNavigation = () => {
  const currentStep = useCurrentStepStore((state) => state.currentStep);
  const navigate = useNavigate();

  const navigableStepPathMap: Record<FormikStep, string> = {
    [FormikStep.VEHICLE_A]: Paths.DataEntry1,
    [FormikStep.VEHICLE_B]: Paths.DataEntry2,
    [FormikStep.ACCIDENT_DATA]: Paths.DataEntry3,
    [FormikStep.SIGNATURE_A]: Paths.DataEntry4,
  };

  useEffect(() => {
    const nextPath = navigableStepPathMap[currentStep];
    if (!nextPath) return;

    const currentPath = window.location.pathname;

    if (currentPath.includes(nextPath)) return;
    navigate(nextPath);
  }, [currentStep]);

  return { currentStep };
};

export const useDataEntryFormik = () => {
  const currentStep = useCurrentStepStore((state) => state.currentStep);
  const goNext = useCurrentStepStore((state) => state.goNextStep);
  const goBack = useCurrentStepStore((state) => state.goBackStep);
  const resetSections = useAccordionStore((state) => state.resetSections);

  // ================ Fetch Draft Data
  const draftId = useDraftIDStore((state) => state.draftID);
  const { crossGate: getVehicleDraft } = useGetVehicleDraft();
  const updateVehicleDraftStore = useDraftCAIStore(
    (state) => state.updateVehicleDraft,
  );
  const { crossGate: getAccidentDataDraft } = useGetAccidentDataDraft();
  const updateAccidentDataDraftStore = useDraftCAIStore(
    (state) => state.updateAccidentDataDraft,
  );

  // ================ Update Draft Data
  const { crossGate: putVehicleDraft } = usePutVehicleDraft();
  const { crossGate: putAccidentDataDraft } = usePutAccidentDataDraft();
  const { crossGate: getVerifyResponsibility } = useVerifyResponsibility();

  const [
    responsabilityModalVisibility,
    showResponsabilityModalVisibility,
    hideResponsabilityModalVisibility,
  ] = useBoolean(false);
  const [responsabilityModalMessage, setResponsabilityModalMessage] =
    useState("");

  const validationSchema = useValidationSchemas()[currentStep];

  const fetchDraftData = async (draftId: string) => {
    //Fetch Vehicle A data
    getVehicleDraft(
      {
        headers: { step: BozzaStepDTO.VEICOLO_A },
        uriParams: { idBozza: draftId },
      },
      {
        onSuccess: (response) => {
          const vehicleData = VehicleMapper.fromDTO(response);
          updateVehicleDraftStore({
            vehicleType: VehicleType.VEHICLE_A,
            vehicleDraft: vehicleData,
          });
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle A draft:", error);
        },
      },
    );

    //Fetch Vehicle B data
    getVehicleDraft(
      {
        headers: { step: BozzaStepDTO.VEICOLO_B },
        uriParams: { idBozza: draftId },
      },
      {
        onSuccess: (response) => {
          const vehicleData = VehicleMapper.fromDTO(response);
          updateVehicleDraftStore({
            vehicleType: VehicleType.VEHICLE_B,
            vehicleDraft: vehicleData,
          });
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle B draft:", error);
        },
      },
    );

    //Fetch Accident Data
    await getAccidentDataDraft(
      {
        uriParams: {
          idBozza: draftId ?? "",
        },
      },
      {
        onSuccess: (response) => {
          const accidentData = AccidentMapper.fromDTO(response);
          updateAccidentDataDraftStore(accidentData);
        },
        onFailure: (error) => {
          console.error("Error fetching accident data draft:", error);
        },
      },
    );
  };

  useEffect(() => {
    if (draftId) fetchDraftData(draftId);

    return () => resetSections();
  }, []);

  // ================ Formik Setup
  const formikCAIState = useDraftCAIStore((state) => state.draft);
  const setFormikCAI = useDraftCAIStore((state) => state.setDraft);

  const formikCAI = useFormik<FormikDraftCAI>({
    initialValues: formikCAIState,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setTouched, setSubmitting, setErrors }) => {
      //Essendo il formik unico per piu pagine, resetta i dati del touched ed errors al cambio pagina
      setTouched({});
      setErrors({});
      setSubmitting(false);
      setFormikCAI(values);

      switch (currentStep) {
        case FormikStep.VEHICLE_A:
        case FormikStep.VEHICLE_B:
          await updateVehicleDraft();
          break;
        case FormikStep.ACCIDENT_DATA:
          await fetchDriversResponsability();
          break;
        default:
          break;
      }
    },
  });

  const { triggerFormikCheck } = useScrollToError(formikCAI);

  const handleFormiksubmit = async () => {
    const errors = await formikCAI.validateForm();

    if (Object.keys(errors).length > 0) {
      formikCAI.setTouched(setNestedObjectValues(errors, true));
      triggerFormikCheck();
      return;
    }

    formikCAI.handleSubmit();
    triggerFormikCheck();
  };

  const onGoBackClick = () => {
    if (currentStep != FormikStep.SIGNATURE_A) {
      const values = formikCAI.values;
      formikCAI.setValues(values);
      setFormikCAI(values);
    }
    goBack();
  };

  const updateVehicleDraft = async () => {
    let stepDto: BozzaStepDTO;
    let body;

    if (currentStep == FormikStep.VEHICLE_A) {
      stepDto = BozzaStepDTO.VEICOLO_A;
      body = VehicleMapper.toDTO(formikCAI.values.vehicleA);
    } else {
      stepDto = BozzaStepDTO.VEICOLO_B;
      body = VehicleMapper.toDTO(formikCAI.values.vehicleB);
    }

    if (!draftId || !body) return;

    await putVehicleDraft(
      {
        uriParams: {
          idBozza: draftId,
        },
        headers: {
          step: stepDto,
        },
        body,
      },
      {
        onSuccess: (_) => {
          goNext();
        },
      },
    );
  };

  const fetchDriversResponsability = async () => {
    const circumstances =
      formikCAI.values.accidentData.vehicleAccidentCircumstances;

    const vehicleAcircumstance = circumstances.find(
      (circumstance) => circumstance.vehicleType == VehicleType.VEHICLE_A,
    );
    const vehicleBcircumstance = circumstances.find(
      (circumstance) => circumstance.vehicleType == VehicleType.VEHICLE_B,
    );

    if (!vehicleAcircumstance || !vehicleBcircumstance) return;

    const body = {
      veicoloA: CircumstancesMapper.toDTO(vehicleAcircumstance),
      veicoloB: CircumstancesMapper.toDTO(vehicleBcircumstance),
    };

    await getVerifyResponsibility(
      {
        body,
      },
      {
        onSuccess: (response) => {
          const result = mapDriverResponsabilitiesResponse({
            response: response.data,
            formikCai: formikCAI.values,
          });
          setResponsabilityModalMessage(result);
          showResponsabilityModalVisibility();
        },
      },
    );
  };

  const updateAccidentDataDraft = async () => {
    const body = AccidentMapper.toDTO(formikCAI.values.accidentData);

    if (!draftId || !body) return;

    await putAccidentDataDraft(
      {
        uriParams: {
          idBozza: draftId,
        },
        body,
      },
      {
        onSuccess: (_) => {
          hideResponsabilityModalVisibility();
          goNext();
        },
      },
    );
  };

  return {
    formikCAI,
    validationSchema,
    responsabilityModalVisibility,
    responsabilityModalMessage,
    hideResponsabilityModalVisibility,
    updateAccidentDataDraft,
    onGoBackClick,
    handleFormiksubmit,
  };
};
